import {TFunction} from 'i18next';
import {snakeCase} from 'lodash';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {setUrl} from 'utils/request';

import {messageKey} from '../components/PortalNotification';

export type SessionStatus = {
    expired: boolean;
    label: string;
};

const buildExpiringIn = (difference: number): Array<number> => {
    const date = new Date();
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(difference / 1000);

    let hours = date.getHours();
    let minutes = date.getMinutes();
    const seconds = date.getSeconds();

    // Round up
    if (minutes + 1 === 60) {
        hours += 1;
        minutes = 0;
    } else if (minutes > 0) {
        minutes += 1;
    }

    return [hours, minutes, seconds];
};

const buildStatus = (
    timestamp: number,
    t: TFunction<'Portal', undefined>,
): SessionStatus => {
    const expiresAt = new Date(timestamp * 1000).getTime();
    const now = Date.now();
    const difference = expiresAt - now;
    const expired = difference < 1;

    if (expired) {
        const message = snakeCase(messageKey.expired_session_token);
        setUrl(`/portal/login/?message=${message}`);

        return {
            expired,
            label: t('sessionTimer.expired'),
        };
    }

    const expiringIn = buildExpiringIn(difference);
    const hours = expiringIn[0];
    const minutes = expiringIn[1];
    const seconds = expiringIn[2];

    let label = '';

    if (hours > 0) {
        label = t('sessionTimer.expiry', {
            hours,
            minutes,
        });
    } else if (minutes > 0) {
        label = t('sessionTimer.expiryMinutesOnly', {minutes});
    } else {
        label = t('sessionTimer.expirySecondsOnly', {seconds});
    }

    return {
        expired,
        label,
    };
};

const useSessionTimer = (expiresAt: number) => {
    const {t} = useTranslation('Portal');
    const [sessionStatus, setSessionStatus] = useState<SessionStatus>(
        buildStatus(expiresAt, t),
    );

    const intervalCallback = () => {
        if (sessionStatus.expired) {
            return;
        }
        const newStatus = buildStatus(expiresAt, t);

        if (newStatus.label === sessionStatus.label) {
            return;
        }
        setSessionStatus(buildStatus(expiresAt, t));
    };

    useEffect(() => {
        const interval = setInterval(intervalCallback, 1000);

        return () => {
            clearInterval(interval);
        };
    });

    return sessionStatus;
};

export default useSessionTimer;
